import { FormEvent, useEffect, useState } from "react";
import { api } from "./lib/api";
import type {
  AdminPlatformData,
  AgencyEvent,
  EventOffer,
  Opportunity,
  User,
} from "./types";

function go(path: string) {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
  window.scrollTo({ top: 0, behavior: "smooth" });
}

function cash(cents: number, currency = "usd") {
  return cents > 0
    ? new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency.toUpperCase(),
      }).format(cents / 100)
    : "Pricing coming soon";
}

function when(value: string, compact = false) {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return "Date to be announced";
  return new Intl.DateTimeFormat(
    "en-US",
    compact
      ? { month: "short", day: "numeric" }
      : {
          weekday: "long",
          month: "long",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
        },
  ).format(date);
}

function Header({ user }: { user: User | null }) {
  const admin = ["admin", "owner", "site_owner"].includes(
    String(user?.role || "").toLowerCase(),
  );
  return (
    <header className="site-header phase-header public-header">
      <a className="skip-link" href="#main-content">Skip to main content</a>
      <button className="wordmark" onClick={() => go("/")}>
        <span className="wordmark-paris">PARIS</span>
        <span>RUNWAY ELITE</span>
      </button>
      <nav className="desktop-nav">
        <button onClick={() => go("/talent")}>Talent</button>
        <button onClick={() => go("/events")}>Events</button>
        <button onClick={() => go("/opportunities")}>Opportunities</button>
        <button onClick={() => go("/#contact")}>Contact</button>
      </nav>
      <button
        className="header-cta"
        onClick={() => go(user ? (admin ? "/admin" : "/dashboard") : "/login")}
      >
        {user ? "My workspace" : "Sign in"} <span>↗</span>
      </button>
    </header>
  );
}

function Footer() {
  return (
    <footer>
      <button className="wordmark wordmark-compact" onClick={() => go("/")}>
        <span className="wordmark-paris">PARIS</span>
        <span>RUNWAY ELITE</span>
      </button>
      <p>Elite · Professional · Empowering</p>
      <p>© {new Date().getFullYear()} Paris Runway Elite Talent Agency</p>
    <p style={{ margin: "1rem 0 0", padding: "0 1rem", textAlign: "center", fontSize: "0.875rem", flexBasis: "100%" }}><a href="https://blissits.com" target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "underline", textUnderlineOffset: "0.2em" }}>Built by Bliss</a></p>
    </footer>
  );
}
function Empty({ title, copy }: { title: string; copy: string }) {
  return (
    <div className="empty-state">
      <span>✦</span>
      <h3>{title}</h3>
      <p>{copy}</p>
    </div>
  );
}

export function EventsPage({
  user,
  eventId,
}: {
  user: User | null;
  eventId?: string;
}) {
  const [events, setEvents] = useState<AgencyEvent[]>([]);
  const [commerceReady, setCommerceReady] = useState(false);
  const [selected, setSelected] = useState<EventOffer | null>(null);
  const [form, setForm] = useState({
    buyer_name: "",
    buyer_email: "",
    organization: "",
    quantity: 1,
  });
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api
      .publicTalent()
      .then((result) => {
        setEvents(result.events);
        setCommerceReady(result.commerce_ready);
      })
      .catch((err) => setError(err.message));
  }, []);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (params.get("checkout") === "success" && sessionId) {
      setBusy(true);
      api
        .eventCheckout("confirm", { session_id: sessionId })
        .then((result) =>
          setNotice(
            result.paid
              ? "Payment confirmed. Your order is reserved."
              : "Your payment is processing. We will confirm it shortly.",
          ),
        )
        .catch((err) => setError(err.message))
        .finally(() => setBusy(false));
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("checkout") === "cancelled")
      setNotice("Checkout was cancelled. Nothing was charged.");
  }, []);
  const event = eventId
    ? events.find((entry) => entry.id === eventId)
    : undefined;
  async function checkout(e: FormEvent) {
    e.preventDefault();
    if (!selected) return;
    setBusy(true);
    setError("");
    try {
      const result = await api.eventCheckout("start", {
        offer_id: selected.id,
        ...form,
        success_url: `${window.location.origin}/events/${eventId}?checkout=success&session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/events/${eventId}?checkout=cancelled`,
      });
      if (result.url) window.location.assign(result.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Checkout is unavailable.");
      setBusy(false);
    }
  }
  if (eventId && events.length && !event)
    return (
      <main className="phase-page">
        <Header user={user} />
        <section className="phase-hero">
          <p className="eyebrow">Agency events</p>
          <h1>Event unavailable.</h1>
          <button className="button button-gold" onClick={() => go("/events")}>
            View all events
          </button>
        </section>
      </main>
    );
  return (
    <main className="phase-page">
      <Header user={user} />
      {event ? (
        <>
          <section className="phase-detail-hero">
            <button className="back-link" onClick={() => go("/events")}>
              ← All events
            </button>
            <p className="eyebrow">
              {event.audience === "all"
                ? "Open agency event"
                : `For ${event.audience}`}
            </p>
            <h1>{event.title}</h1>
            <div className="phase-meta">
              <span>{when(event.starts_at)}</span>
              <span>{event.location || "Location to be announced"}</span>
            </div>
          </section>
          <section className="phase-detail-grid section-pad">
            <article>
              <p className="eyebrow">About the event</p>
              <p className="phase-long-copy">
                {event.description ||
                  "Full event details will be announced by Paris Runway Elite."}
              </p>
            </article>
            <aside>
              <p className="eyebrow">Tickets & participation</p>
              {event.offers?.length ? (
                <div className="offer-purchase-list">
                  {event.offers.map((offer) => (
                    <button
                      key={offer.id}
                      className={selected?.id === offer.id ? "selected" : ""}
                      onClick={() => setSelected(offer)}
                    >
                      <span>{offer.offer_type}</span>
                      <strong>{offer.name}</strong>
                      <p>{offer.description}</p>
                      <em>{cash(offer.price_cents, offer.currency)}</em>
                      <small>
                        {offer.quantity_available === null
                          ? "Available"
                          : `${offer.quantity_available} remaining`}
                      </small>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="phase-muted">
                  Sales options have not been published yet.
                </p>
              )}
              {selected && (
                <form className="purchase-form" onSubmit={checkout}>
                  <h3>Reserve {selected.name}</h3>
                  <label>
                    Full name
                    <input
                      required
                      value={form.buyer_name}
                      onChange={(e) =>
                        setForm({ ...form, buyer_name: e.target.value })
                      }
                    />
                  </label>
                  <label>
                    Email address
                    <input
                      required
                      type="email"
                      value={form.buyer_email}
                      onChange={(e) =>
                        setForm({ ...form, buyer_email: e.target.value })
                      }
                    />
                  </label>
                  {selected.offer_type !== "ticket" && (
                    <label>
                      Organization or business
                      <input
                        required
                        value={form.organization}
                        onChange={(e) =>
                          setForm({ ...form, organization: e.target.value })
                        }
                      />
                    </label>
                  )}
                  <label>
                    Quantity
                    <input
                      type="number"
                      min="1"
                      max={Math.min(25, selected.quantity_available ?? 25)}
                      value={form.quantity}
                      onChange={(e) =>
                        setForm({ ...form, quantity: Number(e.target.value) })
                      }
                    />
                  </label>
                  {error && <div className="form-alert">{error}</div>}
                  <button
                    className="button button-gold"
                    disabled={
                      busy || !commerceReady || selected.price_cents <= 0
                    }
                  >
                    {busy
                      ? "Please wait…"
                      : !commerceReady
                        ? "Secure sales opening soon"
                        : selected.price_cents <= 0
                          ? "Pricing not published"
                          : `Continue to secure checkout · ${cash(selected.price_cents * form.quantity)}`}
                  </button>
                  {!commerceReady && (
                    <small className="checkout-note">
                      The agency is finishing its secure payout setup. No
                      payment can be taken yet.
                    </small>
                  )}
                </form>
              )}
            </aside>
          </section>
        </>
      ) : (
        <>
          <section className="phase-hero">
            <p className="eyebrow">Paris Runway Elite presents</p>
            <h1>
              Train. Audition.
              <br />
              <em>Take the stage.</em>
            </h1>
            <p>
              Agency events, performances, training sessions, and ways to
              participate.
            </p>
          </section>
          <section className="event-index section-pad">
            {notice && <div className="workspace-notice success">{notice}</div>}
            {error && <div className="workspace-notice error">{error}</div>}
            {events.length ? (
              events.map((item) => (
                <button
                  className="event-index-row"
                  key={item.id}
                  onClick={() => go(`/events/${item.id}`)}
                >
                  <time>
                    <strong>{when(item.starts_at, true)}</strong>
                    <span>{new Date(item.starts_at).getFullYear() || ""}</span>
                  </time>
                  <div>
                    <small>{item.audience}</small>
                    <h2>{item.title}</h2>
                    <p>{item.location || "Location to be announced"}</p>
                  </div>
                  <div>
                    <span>
                      {item.offers?.length
                        ? `${item.offers.length} ways to attend`
                        : "Details soon"}
                    </span>
                    <b>↗</b>
                  </div>
                </button>
              ))
            ) : (
              <Empty
                title="New dates are on the way"
                copy="Events will appear here as soon as the agency publishes them."
              />
            )}
          </section>
        </>
      )}
      <Footer />
    </main>
  );
}

export function OpportunitiesPage({
  user,
  opportunityId,
}: {
  user: User | null;
  opportunityId?: string;
}) {
  const [items, setItems] = useState<Opportunity[]>([]);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api
      .publicTalent()
      .then((result) => setItems(result.opportunities))
      .catch((err) => setError(err.message));
  }, []);
  const item = opportunityId
    ? items.find((entry) => entry.id === opportunityId)
    : undefined;
  async function apply(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      await api.applyOpportunity({
        opportunity_id: opportunityId,
        ...Object.fromEntries(form.entries()),
      });
      setNotice(
        "Application received. The agency will contact you if your experience matches the opportunity.",
      );
      event.currentTarget.reset();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Unable to submit application.",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="phase-page">
      <Header user={user} />
      {item ? (
        <>
          <section className="phase-detail-hero opportunity-hero">
            <button className="back-link" onClick={() => go("/opportunities")}>
              ← All opportunities
            </button>
            <p className="eyebrow">{item.opportunity_type}</p>
            <h1>{item.title}</h1>
            <div className="phase-meta">
              <span>{item.location || "Location varies"}</span>
              <span>
                {item.commitment || "Commitment discussed after application"}
              </span>
              {item.deadline && <span>Apply by {item.deadline}</span>}
            </div>
          </section>
          <section className="phase-detail-grid section-pad">
            <article>
              <p className="eyebrow">The opportunity</p>
              <p className="phase-long-copy">
                {item.description || item.summary}
              </p>
              {item.requirements && (
                <>
                  <h3>What we’re looking for</h3>
                  <p className="phase-long-copy smaller">{item.requirements}</p>
                </>
              )}
            </article>
            <aside>
              <form className="opportunity-form" onSubmit={apply}>
                <p className="eyebrow">Apply now</p>
                <h2>Tell us about yourself.</h2>
                <label>
                  Full name
                  <input required name="applicant_name" />
                </label>
                <label>
                  Email
                  <input required name="applicant_email" type="email" />
                </label>
                <div className="form-grid">
                  <label>
                    Phone
                    <input name="applicant_phone" />
                  </label>
                  <label>
                    Age range
                    <input
                      name="age_range"
                      placeholder="18–24, 25+, under 18"
                    />
                  </label>
                </div>
                <label>
                  Availability
                  <textarea required name="availability" rows={3} />
                </label>
                <label>
                  Relevant experience
                  <textarea name="experience" rows={4} />
                </label>
                <label>
                  Why are you interested?
                  <textarea required name="statement" rows={5} />
                </label>
                <label>
                  Portfolio or résumé URL
                  <input name="portfolio_url" type="url" />
                </label>
                {notice && (
                  <div className="workspace-notice success">{notice}</div>
                )}
                {error && <div className="workspace-notice error">{error}</div>}
                <button className="button button-gold" disabled={busy}>
                  {busy ? "Submitting…" : "Submit application"}
                </button>
              </form>
            </aside>
          </section>
        </>
      ) : (
        <>
          <section className="phase-hero">
            <p className="eyebrow">Grow with the movement</p>
            <h1>
              Learn. Contribute.
              <br />
              <em>Build what’s next.</em>
            </h1>
            <p>
              Volunteer and internship opportunities for people who want
              hands-on experience in talent, fashion, media, and events.
            </p>
          </section>
          <section className="opportunity-index section-pad">
            {items.length ? (
              items.map((entry) => (
                <button
                  key={entry.id}
                  onClick={() => go(`/opportunities/${entry.id}`)}
                >
                  <small>{entry.opportunity_type}</small>
                  <h2>{entry.title}</h2>
                  <p>{entry.summary || entry.description}</p>
                  <div>
                    <span>{entry.location || "Location varies"}</span>
                    <span>
                      {entry.deadline
                        ? `Apply by ${entry.deadline}`
                        : "Open until filled"}
                    </span>
                    <b>View opportunity ↗</b>
                  </div>
                </button>
              ))
            ) : (
              <Empty
                title="Opportunities are being prepared"
                copy="Volunteer and internship openings will appear here when applications open."
              />
            )}
          </section>
        </>
      )}
      <Footer />
    </main>
  );
}

export function PhaseTwoAdmin({
  data,
  busy,
  act,
}: {
  data: AdminPlatformData;
  busy: boolean;
  act: (body: Record<string, unknown>, success: string) => Promise<void>;
}) {
  const [view, setView] = useState<"events" | "sales" | "opportunities">(
    "events",
  );
  const [offer, setOffer] = useState<Partial<EventOffer>>({
    offer_type: "ticket",
    status: "published",
    quantity_total: 0,
  });
  const [opportunity, setOpportunity] = useState<Partial<Opportunity>>({
    opportunity_type: "volunteer",
    status: "published",
  });
  return (
    <div className="phase-admin">
      <nav className="sub-tabs">
        <button
          className={view === "events" ? "active" : ""}
          onClick={() => setView("events")}
        >
          Offers
        </button>
        <button
          className={view === "sales" ? "active" : ""}
          onClick={() => setView("sales")}
        >
          Orders <span>{data.orders.length}</span>
        </button>
        <button
          className={view === "opportunities" ? "active" : ""}
          onClick={() => setView("opportunities")}
        >
          Opportunities{" "}
          <span>
            {data.applications.filter((a) => a.status === "new").length}
          </span>
        </button>
      </nav>
      {view === "events" && (
        <div className="admin-layout">
          <section className="workspace-panel">
            <div className="panel-heading">
              <div>
                <p className="workspace-eyebrow">Tickets, vendors & sponsors</p>
                <h2>{offer.id ? "Edit offer" : "Add an event offer"}</h2>
              </div>
            </div>
            <form
              className="workspace-form"
              onSubmit={(e) => {
                e.preventDefault();
                act(
                  {
                    action: "save-offer",
                    ...offer,
                    price_cents: Math.round(Number(offer.price_cents || 0)),
                  },
                  "Event offer saved.",
                ).then(() =>
                  setOffer({
                    offer_type: "ticket",
                    status: "published",
                    quantity_total: 0,
                  }),
                );
              }}
            >
              <label>
                Event
                <select
                  required
                  value={offer.event_id || ""}
                  onChange={(e) =>
                    setOffer({ ...offer, event_id: e.target.value })
                  }
                >
                  <option value="">Choose event</option>
                  {data.events.map((event) => (
                    <option value={event.id} key={event.id}>
                      {event.title}
                    </option>
                  ))}
                </select>
              </label>
              <div className="form-grid">
                <label>
                  Offer type
                  <select
                    value={offer.offer_type || "ticket"}
                    onChange={(e) =>
                      setOffer({
                        ...offer,
                        offer_type: e.target.value as EventOffer["offer_type"],
                      })
                    }
                  >
                    <option value="ticket">Ticket</option>
                    <option value="vendor">Vendor space</option>
                    <option value="sponsorship">Sponsorship</option>
                  </select>
                </label>
                <label>
                  Status
                  <select
                    value={offer.status || "published"}
                    onChange={(e) =>
                      setOffer({
                        ...offer,
                        status: e.target.value as EventOffer["status"],
                      })
                    }
                  >
                    <option value="published">Published</option>
                    <option value="draft">Draft / closed</option>
                  </select>
                </label>
                <label className="full">
                  Name
                  <input
                    required
                    value={offer.name || ""}
                    onChange={(e) =>
                      setOffer({ ...offer, name: e.target.value })
                    }
                  />
                </label>
                <label>
                  Price ($)
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={((offer.price_cents || 0) / 100).toFixed(2)}
                    onChange={(e) =>
                      setOffer({
                        ...offer,
                        price_cents: Math.round(Number(e.target.value) * 100),
                      })
                    }
                  />
                  <small>Enter the customer-facing dollar amount</small>
                </label>
                <label>
                  Quantity
                  <input
                    type="number"
                    min="0"
                    value={offer.quantity_total || 0}
                    onChange={(e) =>
                      setOffer({
                        ...offer,
                        quantity_total: Number(e.target.value),
                      })
                    }
                  />
                  <small>Use 0 for unlimited</small>
                </label>
                <label className="full">
                  Description
                  <textarea
                    rows={3}
                    value={offer.description || ""}
                    onChange={(e) =>
                      setOffer({ ...offer, description: e.target.value })
                    }
                  />
                </label>
              </div>
              <button className="workspace-primary" disabled={busy}>
                Save offer
              </button>
            </form>
          </section>
          <section className="workspace-panel">
            <div className="panel-heading">
              <div>
                <p className="workspace-eyebrow">Published inventory</p>
                <h2>All offers</h2>
              </div>
            </div>
            {data.offers.length ? (
              <div className="admin-record-list">
                {data.offers.map((item) => (
                  <article key={item.id}>
                    <div>
                      <small>
                        {item.offer_type} ·{" "}
                        {data.events.find((e) => e.id === item.event_id)?.title}
                      </small>
                      <strong>{item.name}</strong>
                      <span>
                        {cash(item.price_cents)} · {item.quantity_sold} sold ·{" "}
                        {item.quantity_total || "Unlimited"} total
                      </span>
                    </div>
                    <span className={`status status-${item.status}`}>
                      {item.status}
                    </span>
                    <button onClick={() => setOffer(item)}>Edit</button>
                    <button
                      className="danger-link"
                      onClick={() =>
                        act(
                          { action: "delete-offer", id: item.id },
                          "Offer removed.",
                        )
                      }
                    >
                      Delete
                    </button>
                  </article>
                ))}
              </div>
            ) : (
              <Empty
                title="No offers yet"
                copy="Create an event first, then publish tickets, vendor spaces, or sponsorships."
              />
            )}
          </section>
        </div>
      )}
      {view === "sales" && (
        <section className="workspace-panel">
          <div className="panel-heading">
            <div>
              <p className="workspace-eyebrow">Event commerce</p>
              <h2>Orders and fulfillment</h2>
            </div>
            <span>Stripe remains off until connected</span>
          </div>
          {data.orders.length ? (
            <div className="admin-record-list order-list">
              {data.orders.map((order) => (
                <article key={order.id}>
                  <div>
                    <small>
                      {order.event_title} · {order.offer_name}
                    </small>
                    <strong>{order.buyer_name}</strong>
                    <span>
                      {order.buyer_email}
                      {order.organization ? ` · ${order.organization}` : ""}
                    </span>
                    <span>
                      {order.quantity} ×{" "}
                      {cash(
                        order.total_amount_cents / order.quantity,
                        order.currency,
                      )}{" "}
                      = {cash(order.total_amount_cents, order.currency)}
                    </span>
                  </div>
                  <span className={`status status-${order.payment_status}`}>
                    {order.payment_status}
                  </span>
                  <label>
                    Fulfillment
                    <select
                      value={order.fulfillment_status}
                      onChange={(e) =>
                        act(
                          {
                            action: "update-order",
                            id: order.id,
                            fulfillment_status: e.target.value,
                          },
                          "Order fulfillment updated.",
                        )
                      }
                    >
                      <option value="pending">Pending</option>
                      <option value="ready">Ready</option>
                      <option value="fulfilled">Fulfilled</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </label>
                </article>
              ))}
            </div>
          ) : (
            <Empty
              title="No orders yet"
              copy="Paid event orders will appear here after the agency connects Stripe and opens sales."
            />
          )}
        </section>
      )}
      {view === "opportunities" && (
        <div className="admin-layout">
          <section className="workspace-panel">
            <div className="panel-heading">
              <div>
                <p className="workspace-eyebrow">Volunteer & internship</p>
                <h2>
                  {opportunity.id ? "Edit opportunity" : "Create opportunity"}
                </h2>
              </div>
            </div>
            <form
              className="workspace-form"
              onSubmit={(e) => {
                e.preventDefault();
                act(
                  { action: "save-opportunity", ...opportunity },
                  "Opportunity saved.",
                ).then(() =>
                  setOpportunity({
                    opportunity_type: "volunteer",
                    status: "published",
                  }),
                );
              }}
            >
              <div className="form-grid">
                <label>
                  Type
                  <select
                    value={opportunity.opportunity_type || "volunteer"}
                    onChange={(e) =>
                      setOpportunity({
                        ...opportunity,
                        opportunity_type: e.target
                          .value as Opportunity["opportunity_type"],
                      })
                    }
                  >
                    <option value="volunteer">Volunteer</option>
                    <option value="internship">Internship</option>
                  </select>
                </label>
                <label>
                  Status
                  <select
                    value={opportunity.status || "published"}
                    onChange={(e) =>
                      setOpportunity({
                        ...opportunity,
                        status: e.target.value as Opportunity["status"],
                      })
                    }
                  >
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                    <option value="closed">Closed</option>
                  </select>
                </label>
                <label className="full">
                  Title
                  <input
                    required
                    value={opportunity.title || ""}
                    onChange={(e) =>
                      setOpportunity({ ...opportunity, title: e.target.value })
                    }
                  />
                </label>
                <label className="full">
                  Short summary
                  <textarea
                    rows={2}
                    value={opportunity.summary || ""}
                    onChange={(e) =>
                      setOpportunity({
                        ...opportunity,
                        summary: e.target.value,
                      })
                    }
                  />
                </label>
                <label className="full">
                  Description
                  <textarea
                    rows={5}
                    value={opportunity.description || ""}
                    onChange={(e) =>
                      setOpportunity({
                        ...opportunity,
                        description: e.target.value,
                      })
                    }
                  />
                </label>
                <label className="full">
                  Requirements
                  <textarea
                    rows={4}
                    value={opportunity.requirements || ""}
                    onChange={(e) =>
                      setOpportunity({
                        ...opportunity,
                        requirements: e.target.value,
                      })
                    }
                  />
                </label>
                <label>
                  Location
                  <input
                    value={opportunity.location || ""}
                    onChange={(e) =>
                      setOpportunity({
                        ...opportunity,
                        location: e.target.value,
                      })
                    }
                  />
                </label>
                <label>
                  Commitment
                  <input
                    value={opportunity.commitment || ""}
                    onChange={(e) =>
                      setOpportunity({
                        ...opportunity,
                        commitment: e.target.value,
                      })
                    }
                  />
                </label>
                <label>
                  Deadline
                  <input
                    type="date"
                    value={opportunity.deadline || ""}
                    onChange={(e) =>
                      setOpportunity({
                        ...opportunity,
                        deadline: e.target.value,
                      })
                    }
                  />
                </label>
              </div>
              <button className="workspace-primary" disabled={busy}>
                Save opportunity
              </button>
            </form>
            <div className="admin-record-list compact-records">
              {data.opportunities.map((item) => (
                <article key={item.id}>
                  <div>
                    <small>{item.opportunity_type}</small>
                    <strong>{item.title}</strong>
                    <span>{item.application_count} applications</span>
                  </div>
                  <span className={`status status-${item.status}`}>
                    {item.status}
                  </span>
                  <button onClick={() => setOpportunity(item)}>Edit</button>
                  <button
                    className="danger-link"
                    onClick={() =>
                      act(
                        { action: "delete-opportunity", id: item.id },
                        "Opportunity removed.",
                      )
                    }
                  >
                    Delete
                  </button>
                </article>
              ))}
            </div>
          </section>
          <section className="workspace-panel">
            <div className="panel-heading">
              <div>
                <p className="workspace-eyebrow">Applicant review</p>
                <h2>Applications</h2>
              </div>
            </div>
            {data.applications.length ? (
              <div className="application-list">
                {data.applications.map((application) => (
                  <article key={application.id}>
                    <small>
                      {application.opportunity_title} ·{" "}
                      {new Date(
                        application.created_at * 1000,
                      ).toLocaleDateString()}
                    </small>
                    <h3>{application.applicant_name}</h3>
                    <a href={`mailto:${application.applicant_email}`}>
                      {application.applicant_email}
                    </a>
                    <p>{application.statement}</p>
                    {application.availability && (
                      <p>
                        <strong>Availability:</strong>{" "}
                        {application.availability}
                      </p>
                    )}
                    <label>
                      Status
                      <select
                        value={application.status}
                        onChange={(e) =>
                          act(
                            {
                              action: "review-application",
                              id: application.id,
                              status: e.target.value,
                              admin_notes: application.admin_notes,
                            },
                            "Application updated.",
                          )
                        }
                      >
                        <option value="new">New</option>
                        <option value="reviewing">Reviewing</option>
                        <option value="accepted">Accepted</option>
                        <option value="declined">Declined</option>
                      </select>
                    </label>
                    <label>
                      Admin notes
                      <textarea
                        defaultValue={application.admin_notes}
                        onBlur={(e) =>
                          act(
                            {
                              action: "review-application",
                              id: application.id,
                              status: application.status,
                              admin_notes: e.target.value,
                            },
                            "Application notes saved.",
                          )
                        }
                      />
                    </label>
                  </article>
                ))}
              </div>
            ) : (
              <Empty
                title="No applications yet"
                copy="New volunteer and internship applications will appear here."
              />
            )}
          </section>
        </div>
      )}
    </div>
  );
}
